/**
 * 🔗 Supabase Client - 프론트엔드 Supabase 클라이언트
 *
 * 🎯 핵심 철학:
 * - 환경 변수에서 Supabase 설정 로드
 * - anon key를 사용한 클라이언트 생성
 * - Row Level Security (RLS) 정책 준수
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../../../../shared/logger';

// ============================================
// 🌍 환경 변수에서 Supabase 설정 로드
// ============================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// ============================================
// 🔍 설정 검증
// ============================================

function validateSupabaseConfig(): boolean {
  if (!supabaseUrl) {
    logger.error('❌ VITE_SUPABASE_URL이 설정되지 않았습니다.');
    logger.error('   frontend/.env 파일에 VITE_SUPABASE_URL을 입력하세요.');
    return false;
  }

  if (!supabaseAnonKey) {
    logger.error('❌ VITE_SUPABASE_ANON_KEY가 설정되지 않았습니다.');
    logger.error('   frontend/.env 파일에 VITE_SUPABASE_ANON_KEY를 입력하세요.');
    return false;
  }

  return true;
}

// ============================================
// 🔗 Supabase Client (프론트엔드)
// ============================================

/**
 * 프론트엔드에서 사용하는 Supabase 클라이언트
 * anon key를 사용하여 RLS 정책에 따라 데이터 접근
 */
let supabaseInstance: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!supabaseInstance) {
    if (!validateSupabaseConfig()) {
      throw new Error('Supabase 설정이 올바르지 않습니다. 환경 변수를 확인하세요.');
    }

    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });

    logger.debug('✅ Supabase 클라이언트 초기화 완료:', {
      data: {
        url: supabaseUrl,
        hasAnonKey: !!supabaseAnonKey,
      },
    });
  }

  return supabaseInstance;
}

// ============================================
// 🔄 기본 export
// ============================================

// 기본 클라이언트 인스턴스 export (편의를 위해)
export const supabase = getSupabase();

export default supabase;
